import { ChangeEvent, useCallback } from 'react';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import { NOOP } from 'config/constants';
import { Colors } from 'styles';

export const SettingsMenuItem = styled(MenuItem)({
  '&.Mui-selected': {
    backgroundColor: 'initial',
  },
});

type SettingsDropdownOpenStyle = 'default' | 'highlighted';

type SettingsDropdownProps = {
  multiple?: boolean;
  children?: React.ReactNode;
  value?: string | number | string[] | ReadonlyArray<string>;
  onSelectedChange?: (v: string | number | string[] | ReadonlyArray<string>) => unknown;
  onClose?: (evt: ChangeEvent<object>) => void;
  onOpen?: () => void;
  open?: boolean;
  menuId?: string;
  disabled?: boolean;
  displayEmpty?: boolean;
  renderValue?: SelectProps['renderValue'];
  openStyle?: SettingsDropdownOpenStyle;
  ['data-testid']?: string;
};

export default function SettingsDropdown({
  children = [],
  value,
  onSelectedChange = NOOP,
  onClose = NOOP,
  onOpen,
  open,
  menuId = '',
  disabled = false,
  multiple = false,
  renderValue,
  openStyle = 'default',
  'data-testid': dataTestId,
  displayEmpty = false,
}: SettingsDropdownProps): React.ReactElement {
  const handleOnChange = useCallback(
    (event: SelectChangeEvent<string | number | readonly string[]>) => {
      onSelectedChange(event.target.value as any);
    },
    [onSelectedChange]
  );

  const defaultValue = multiple ? [] : '';
  const openHighlightSx =
    openStyle === 'highlighted'
      ? {
          backgroundColor: Colors.gray4,
          boxShadow: `0 0 0 1px ${Colors.mainBlue}`,
        }
      : undefined;
  const resolvedRenderValue =
    renderValue ??
    (multiple
      ? (v: any) => {
          if (Array.isArray(v)) return v.join(', ');

          if (displayEmpty) {
            return 'None';
          }

          return '';
        }
      : undefined);

  return (
    <Select
      disabled={disabled}
      multiple={multiple}
      value={value ?? defaultValue}
      displayEmpty={displayEmpty}
      onChange={handleOnChange}
      onClose={onClose}
      onOpen={onOpen}
      open={open}
      data-testid={dataTestId}
      sx={{
        mt: '0.5rem',

        '& fieldset': {
          border: '0',
        },

        '&:hover fieldset': {
          border: '0',
        },

        '&.Mui-focused fieldset': {
          border: '0',
        },

        '& .MuiSelect-select': {
          overflow: 'hidden',
          border: '0',
          backgroundColor: Colors.gray5,
          fontSize: '1.4rem',
          lineHeight: '1.57',
          color: Colors.gray10,
          padding: '0.5rem 1rem',
          display: 'flex',
          alignItems: 'center',
        },

        '& .MuiSelect-select[aria-expanded="true"]': openHighlightSx,
      }}
      MenuProps={{
        id: menuId,
        slotProps: {
          paper: {
            sx: {
              mt: '0.5rem',
              backgroundColor: Colors.gray5,
              fontWeight: 500,
              fontStretch: 'normal',
              fontStyle: 'normal',
              color: Colors.gray10,
            },
          },
        },
      }}
      input={<OutlinedInput />}
      renderValue={resolvedRenderValue}
    >
      {children}
    </Select>
  );
}
